import { CaseTask } from "./types";

export type TaskAction =
  | {
      type: "upload";
      documentKind: string;
      label: string;
      applicationUuid?: string;
    }
  | { type: "form"; to: string }
  | { type: "resubmit" }
  | { type: "consent"; label: string; confirmText: string };

interface TaskKind {
  info: (task: CaseTask) => (string | undefined)[];
  action?: (task: CaseTask) => TaskAction | undefined;
}

export const contextValue = (task: CaseTask, key: string) =>
  task.context?.properties?.[key]?.default;

export const contextText = (task: CaseTask, key: string) => {
  const value = contextValue(task, key);
  return typeof value === "string" && value ? value : undefined;
};

const noteFromAbound = (task: CaseTask) =>
  contextText(task, "note") && `Note from Abound: ${contextText(task, "note")}`;

// The backend sends absolute links into this app; route to them in-app.
const appPath = (url: string) => {
  const { pathname, search, hash } = new URL(url, window.location.origin);
  return `${pathname}${search}${hash}`;
};

// Keyed by `kind`. Each closes on the backend once the broker has done it: uploading
// the document, submitting the form, resubmitting, or recording consent.
const TASK_KINDS: Record<string, TaskKind> = {
  upload_document: {
    info: (task) => [
      `Abound needs ${
        contextText(task, "document_kind_label") ?? "a document"
      } for this application.`,
      noteFromAbound(task),
    ],
    action: (task) => {
      const documentKind = contextText(task, "document_kind");
      const filing = contextValue(task, "filing") as
        | { application_uuid?: string | null }
        | undefined;
      return documentKind
        ? {
            type: "upload",
            documentKind,
            label: contextText(task, "document_kind_label") ?? "Document",
            applicationUuid: filing?.application_uuid ?? undefined,
          }
        : undefined;
    },
  },
  complete_form: {
    info: (task) => [
      `${
        contextText(task, "label") ?? "A form"
      } needs completing for this application. This task closes automatically once the form is complete.`,
      noteFromAbound(task),
    ],
    action: (task) => {
      const url = contextText(task, "url");
      return url ? { type: "form", to: appPath(url) } : undefined;
    },
  },
  resubmit_application: {
    info: (task) => [contextText(task, "asked")],
    action: () => ({ type: "resubmit" }),
  },
  broker_consent: {
    info: () => [
      "Abound needs you to confirm that your customer has given consent for this application.",
    ],
    action: () => ({
      type: "consent",
      label: "Confirm consent",
      confirmText:
        "Please confirm that your customer has given consent for this application.",
    }),
  },
};

const fallbackKind: TaskKind = {
  info: (task) => [contextText(task, "note")],
};

export const taskInfo = (task: CaseTask) =>
  (TASK_KINDS[task.kind] ?? fallbackKind)
    .info(task)
    .filter((text): text is string => !!text);

export const taskAction = (task: CaseTask) =>
  task.status === "OPEN" ? TASK_KINDS[task.kind]?.action?.(task) : undefined;
